// Shared occupancy classification for the public and admin calendars so the
// two views agree on what "amber", "full", "closed", or "staffed" looks like.

export type CellTone = 'closed' | 'staffed' | 'full' | 'amber' | 'green';

export function occupancyTone(totalBeds: number, occupied: number): CellTone {
  if (totalBeds === 0) return 'closed';
  if (occupied >= totalBeds) return 'full';
  const freeRatio = (totalBeds - occupied) / totalBeds;
  return freeRatio < 0.5 ? 'amber' : 'green';
}

/**
 * Tone for a cell that knows about staffing. Priority:
 *   closed   — strongest signal; hatched, never overridden
 *   staffed  — sky tint when a warden is on duty, regardless of bed
 *              count (the user-facing signal is "someone is at the hut")
 *   full / amber / green — fallback to plain occupancy.
 */
export function cellTone(
  totalBeds: number,
  occupied: number,
  staffed: boolean,
): CellTone {
  if (totalBeds === 0) return 'closed';
  if (staffed) return 'staffed';
  return occupancyTone(totalBeds, occupied);
}

export const CELL_CLASSES: Record<CellTone, string> = {
  green:
    'bg-emerald-50 border-emerald-300 text-emerald-900 ' +
    'dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200',
  amber:
    'bg-amber-50 border-amber-300 text-amber-900 ' +
    'dark:bg-amber-950 dark:border-amber-700 dark:text-amber-200',
  full:
    'bg-rose-50 border-rose-300 text-rose-900 ' +
    'dark:bg-rose-950 dark:border-rose-700 dark:text-rose-200',
  // Staffed days take a sky tint — same hue family as the retired
  // "Hut staffed" pill. Overrides occupancy colour because "someone is
  // at the hut" is the headline signal; the bed count is still visible
  // via the per-cell badge.
  staffed:
    'bg-sky-50 border-sky-300 text-sky-900 ' +
    'dark:bg-sky-950 dark:border-sky-700 dark:text-sky-200',
  // Diagonal-stripe pattern, also legible without colour. Dark variant
  // uses slate-700/800 alternation against the dark page bg.
  closed:
    'border-slate-300 text-slate-600 ' +
    'bg-[repeating-linear-gradient(45deg,theme(colors.slate.200)_0,theme(colors.slate.200)_4px,theme(colors.slate.100)_4px,theme(colors.slate.100)_8px)] ' +
    'dark:border-slate-600 dark:text-slate-300 ' +
    'dark:bg-[repeating-linear-gradient(45deg,theme(colors.slate.700)_0,theme(colors.slate.700)_4px,theme(colors.slate.800)_4px,theme(colors.slate.800)_8px)]',
};
